import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Platform } from "react-native";

import {
  applyFinderDraftPatch,
  detachFinderMatch,
  emptyFinderDraft,
  FINDER_LOCK_CLEAR_FAILED,
  FINDER_LOCK_WRITE_FAILED,
  FINDER_MATCH_DETACHED,
  finderDraftHasName,
  finderDraftHasPayload,
  finderHardwareBackPhase,
  FINDER_MATCH_PRIVACY_RESET,
  finderIdentityChanged,
  finderMatchBaselineDeviates,
  finderPersonUnchanged,
  type FinderDraft,
} from "@/ui/finder/finderMode";
import {
  clearFinderModeLock,
  noteFinderLockHeld,
  onFinderLockCleared,
  onFinderLockStorage,
  readFinderLock,
  readFinderLockSync,
  writeFinderLock,
  writeFinderLockSync,
} from "@/ui/finder/finderModeLock";

export type FinderPhase = "editing" | "confirmKeep" | "confirmDrop" | "handover";

type FinderModeContextValue = {
  /** true, sobald das Aktiv-Flag gelesen ist. Vorher ist die App zugedeckt. */
  storageReady: boolean;
  /** App-Bedienung zu: Flag noch ungelesen oder Findermodus aktiv. */
  blocking: boolean;
  active: boolean;
  phase: FinderPhase | null;
  draft: FinderDraft;
  /** „Fertig“ ohne Namen: Hinweis am Namensfeld, kein Dialog. */
  nameHint: boolean;
  /** Nach dem Übergabe-Screen, bis der Entwurf geleert wird. */
  returned: boolean;
  /** Steigt, wenn die Finder-Karte in den Blick soll. */
  focusToken: number;
  /** Pflicht zur Bereitstellungsart: Hinweis erst nach einem Speicherversuch. */
  provisionAlert: boolean;
  /** Steigt mit jedem Speicherversuch ohne Art, damit der Picker den Fokus erhält. */
  provisionFocus: number;
  /** Schreiben der Sperre ist fehlgeschlagen. Der Modus bleibt aus. */
  lockError: string | null;
  /** Rückgabe ist im Tab frei, der Gerätespeicher konnte nicht geleert werden. */
  handoverNote: string | null;
  /** Sperre beim Start gelesen, Entwurf im Speicher war leer (Neuladen). */
  recoveredWithoutDraft: boolean;
  start: (seed?: Partial<FinderDraft>) => Promise<void>;
  updateDraft: (patch: Partial<FinderDraft>) => void;
  requestFinish: () => void;
  requestDiscard: () => void;
  confirmKeep: () => void;
  confirmDiscard: () => void;
  cancelDialog: () => void;
  hardwareBack: () => void;
  completeHandover: () => void;
  requestScrollToFinder: () => void;
  noteProvisionAttempt: () => void;
  clearDraft: () => void;
  /** Nach einem Treffer einer anderen Person, bis die Bestätigung neu gesetzt wird. */
  matchPrivacyNotice: string | null;
  /** Nach dem Lösen eines Treffers, bis ein neuer Treffer oder ein leerer Entwurf kommt. */
  matchDetachNotice: string | null;
  releaseMatchedContact: () => void;
};

const FinderModeContext = createContext<FinderModeContextValue | null>(null);

function applyLockHandover(): {
  active: boolean;
  phase: FinderPhase | null;
} {
  return { active: true, phase: "handover" };
}

export function FinderModeProvider({
  children,
  userId = null,
}: {
  children: React.ReactNode;
  userId?: string | null;
}) {
  const initialLock = readFinderLockSync(userId);
  const [storageReady, setStorageReady] = useState(initialLock !== null);
  const [active, setActive] = useState(initialLock === true);
  const [phase, setPhase] = useState<FinderPhase | null>(
    initialLock === true ? "handover" : null,
  );
  const [draft, setDraft] = useState<FinderDraft>(emptyFinderDraft);
  const [nameHint, setNameHint] = useState(false);
  const [returned, setReturned] = useState(false);
  const [focusToken, setFocusToken] = useState(0);
  const [provisionAlert, setProvisionAlert] = useState(false);
  const [provisionFocus, setProvisionFocus] = useState(0);
  const [lockError, setLockError] = useState<string | null>(null);
  const [handoverNote, setHandoverNote] = useState<string | null>(null);
  const [recoveredWithoutDraft, setRecoveredWithoutDraft] = useState(initialLock === true);
  const [matchPrivacyNotice, setMatchPrivacyNotice] = useState<string | null>(null);
  const [matchDetachNotice, setMatchDetachNotice] = useState<string | null>(null);
  const draftRef = useRef(draft);
  const activeRef = useRef(active);
  const sessionStarted = useRef(initialLock === true);
  const userIdRef = useRef(userId);
  const seenUser = useRef(userId);
  draftRef.current = draft;
  activeRef.current = active;
  userIdRef.current = userId;

  const commitDraft = useCallback((next: FinderDraft) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  const resetDraft = useCallback(() => {
    commitDraft(emptyFinderDraft());
    setMatchPrivacyNotice(null);
    setMatchDetachNotice(null);
    setProvisionAlert(false);
  }, [commitDraft]);

  useEffect(() => {
    const id = userId;
    if (seenUser.current !== id) {
      seenUser.current = id;
      sessionStarted.current = false;
      setActive(false);
      setPhase(null);
      setStorageReady(false);
      setRecoveredWithoutDraft(false);
    }
    if (!id) return;
    if (Platform.OS === "web") {
      if (sessionStarted.current) return;
      if (readFinderLockSync(id) === true) {
        const next = applyLockHandover();
        resetDraft();
        setNameHint(false);
        setRecoveredWithoutDraft(true);
        setActive(next.active);
        setPhase(next.phase);
      }
      setStorageReady(true);
      return;
    }
    let cancelled = false;
    void readFinderLock(id).then((locked) => {
      if (cancelled || sessionStarted.current) {
        if (!cancelled) setStorageReady(true);
        return;
      }
      if (locked) {
        const next = applyLockHandover();
        resetDraft();
        setNameHint(false);
        setRecoveredWithoutDraft(true);
        setActive(next.active);
        setPhase(next.phase);
      }
      setStorageReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, resetDraft]);

  useEffect(() => onFinderLockCleared((clearedUserId) => {
    if (userIdRef.current && clearedUserId !== userIdRef.current) return;
    sessionStarted.current = false;
    setActive(false);
    setPhase(null);
    setStorageReady(true);
  }), []);

  useEffect(() => onFinderLockStorage((eventUserId, locked) => {
    if (!userIdRef.current || eventUserId !== userIdRef.current) return;
    if (locked) {
      sessionStarted.current = true;
      resetDraft();
      setNameHint(false);
      setLockError(null);
      setActive(true);
      setPhase("handover");
      setStorageReady(true);
      return;
    }
    sessionStarted.current = false;
    setActive(false);
    setPhase(null);
    setStorageReady(true);
  }), [resetDraft]);

  const applyStarted = useCallback((seed?: Partial<FinderDraft>) => {
    setLockError(null);
    setHandoverNote(null);
    setRecoveredWithoutDraft(false);
    setMatchPrivacyNotice(null);
    sessionStarted.current = true;
    const next = applyFinderDraftPatch(draftRef.current, seed ?? {});
    next.privacyDocumented = false;
    commitDraft(next);
    setNameHint(false);
    setPhase("editing");
    setActive(true);
    activeRef.current = true;
  }, [commitDraft]);

  const start = useCallback((seed?: Partial<FinderDraft>) => {
    const id = userIdRef.current;
    if (!id) {
      setLockError(FINDER_LOCK_WRITE_FAILED);
      return Promise.resolve();
    }
    if (Platform.OS === "web") {
      if (!writeFinderLockSync(id, true)) {
        setLockError(FINDER_LOCK_WRITE_FAILED);
        return Promise.resolve();
      }
      noteFinderLockHeld(id);
      applyStarted(seed);
      return Promise.resolve();
    }
    return writeFinderLock(id, true).then((ok) => {
      if (!ok) {
        setLockError(FINDER_LOCK_WRITE_FAILED);
        return;
      }
      noteFinderLockHeld(id);
      applyStarted(seed);
    });
  }, [applyStarted]);

  const updateDraft = useCallback((patch: Partial<FinderDraft>) => {
    const prev = draftRef.current;
    const next = applyFinderDraftPatch(prev, patch);
    const applyingMatch =
      patch.matchedContactId != null && patch.matchedContactId !== prev.matchedContactId;
    const samePerson = finderPersonUnchanged(prev, patch);
    if (
      !activeRef.current &&
      finderIdentityChanged(prev, patch) &&
      !(applyingMatch && samePerson)
    ) {
      next.privacyDocumented = false;
    }
    let detached = false;
    if (
      !applyingMatch &&
      next.matchedContactId &&
      next.matchedBaseline &&
      finderMatchBaselineDeviates(next, next.matchedBaseline)
    ) {
      const released = detachFinderMatch(next, {
        keepClientContactId: patch.clientContactId !== undefined,
      });
      next.matchedContactId = released.matchedContactId;
      next.matchedBaseline = released.matchedBaseline;
      next.clientContactId = released.clientContactId;
      detached = true;
    }
    commitDraft(next);
    if (detached) setMatchDetachNotice(FINDER_MATCH_DETACHED);
    if (!activeRef.current && applyingMatch) {
      setMatchPrivacyNotice(samePerson ? null : FINDER_MATCH_PRIVACY_RESET);
      setMatchDetachNotice(null);
    } else if (patch.privacyDocumented === true) {
      setMatchPrivacyNotice(null);
    }
    if (patch.displayName != null && patch.displayName.trim().length > 0) {
      setNameHint(false);
    }
    if (patch.privacyProvisionKind != null || patch.privacyDocumented === false) {
      setProvisionAlert(false);
    }
  }, [commitDraft]);

  const requestFinish = useCallback(() => {
    if (!finderDraftHasName(draft)) {
      setNameHint(true);
      setPhase("editing");
      return;
    }
    setNameHint(false);
    setPhase("confirmKeep");
  }, [draft]);

  const requestDiscard = useCallback(() => {
    if (!finderDraftHasPayload(draft)) {
      resetDraft();
      setNameHint(false);
      setPhase("handover");
      return;
    }
    setPhase("confirmDrop");
  }, [draft, resetDraft]);

  const confirmKeep = useCallback(() => {
    setNameHint(false);
    setPhase("handover");
  }, []);

  const confirmDiscard = useCallback(() => {
    resetDraft();
    setNameHint(false);
    setPhase("handover");
  }, [resetDraft]);

  const cancelDialog = useCallback(() => {
    setPhase("editing");
  }, []);

  const hardwareBack = useCallback(() => {
    if (!active || phase == null || phase === "handover") return;
    if (phase === "confirmKeep" || phase === "confirmDrop") {
      setPhase("editing");
      return;
    }
    const next = finderHardwareBackPhase(draft);
    if (next === "handover") {
      resetDraft();
      setNameHint(false);
    }
    setPhase(next);
  }, [active, phase, draft, resetDraft]);

  const completeHandover = useCallback(() => {
    const cleared = clearFinderModeLock(userIdRef.current);
    sessionStarted.current = false;
    setPhase(null);
    setActive(false);
    setReturned(true);
    setHandoverNote(null);
    setFocusToken((n) => n + 1);
    void Promise.resolve(cleared).then((ok) => {
      if (!ok) setHandoverNote(FINDER_LOCK_CLEAR_FAILED);
    });
  }, []);

  const requestScrollToFinder = useCallback(() => {
    setFocusToken((n) => n + 1);
  }, []);

  const noteProvisionAttempt = useCallback(() => {
    setProvisionAlert(true);
    setProvisionFocus((n) => n + 1);
  }, []);

  const releaseMatchedContact = useCallback(() => {
    const prev = draftRef.current;
    if (!prev.matchedContactId) return;
    commitDraft(detachFinderMatch(prev));
    setMatchDetachNotice(FINDER_MATCH_DETACHED);
  }, [commitDraft]);

  const clearDraft = useCallback(() => {
    resetDraft();
    setActive(false);
    setPhase(null);
    setNameHint(false);
    setReturned(false);
    setHandoverNote(null);
    setRecoveredWithoutDraft(false);
  }, [resetDraft]);

  const blocking = !storageReady || active;

  const value = useMemo(
    () => ({
      storageReady,
      blocking,
      active,
      phase,
      draft,
      nameHint,
      returned,
      focusToken,
      provisionAlert,
      provisionFocus,
      lockError,
      handoverNote,
      recoveredWithoutDraft,
      matchPrivacyNotice,
      matchDetachNotice,
      releaseMatchedContact,
      start,
      updateDraft,
      requestFinish,
      requestDiscard,
      confirmKeep,
      confirmDiscard,
      cancelDialog,
      hardwareBack,
      completeHandover,
      requestScrollToFinder,
      noteProvisionAttempt,
      clearDraft,
    }),
    [
      storageReady,
      blocking,
      active,
      phase,
      draft,
      nameHint,
      returned,
      focusToken,
      provisionAlert,
      provisionFocus,
      lockError,
      handoverNote,
      recoveredWithoutDraft,
      matchPrivacyNotice,
      matchDetachNotice,
      releaseMatchedContact,
      start,
      updateDraft,
      requestFinish,
      requestDiscard,
      confirmKeep,
      confirmDiscard,
      cancelDialog,
      hardwareBack,
      completeHandover,
      requestScrollToFinder,
      noteProvisionAttempt,
      clearDraft,
    ],
  );

  return (
    <FinderModeContext.Provider value={value}>
      {children}
    </FinderModeContext.Provider>
  );
}

export function useFinderMode(): FinderModeContextValue {
  const ctx = useContext(FinderModeContext);
  if (!ctx) {
    throw new Error("useFinderMode requires FinderModeProvider");
  }
  return ctx;
}

export function useOptionalFinderMode(): FinderModeContextValue | null {
  return useContext(FinderModeContext);
}

/**
 * Fall ist schon angelegt, Finder noch im Provider: beim Verlassen leeren,
 * damit der Entwurf nicht an der nächsten Aufnahme hängen bleibt.
 */
export function useClearFinderDraftAfterSavedCase(
  savedCaseIds: string[] | null,
  clearDraft: () => void,
): void {
  const savedRef = useRef(savedCaseIds);
  savedRef.current = savedCaseIds;
  const clearRef = useRef(clearDraft);
  clearRef.current = clearDraft;
  useEffect(() => {
    return () => {
      if (savedRef.current && savedRef.current.length > 0) clearRef.current();
    };
  }, []);
}
